import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button, Form, Alert } from 'react-bootstrap';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import './UserSettings.scss';
import { useAuth } from '../../context/AuthContext';

const UserSettings = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [user, setUser] = useState({
    user_name: '',
    user_first_name: '',
    user_last_name: ''
  });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/signin');
    } else {
      fetchUserDetails();
    }
  }, [isAuthenticated]);

  const fetchUserDetails = async () => {
    try {
      const response = await axios.get('/users-api/user/settings');
      setUser(response.data.user);
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUser((prevUser) => ({
      ...prevUser,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password && password !== confirmPassword) {
      setError('Password does not match.');
      return;
    }

    setIsSaving(true);
    try {
      const response = await axios.put('/users-api/user/settings', {
        ...user,
        password
      });
      if (response.status === 200) {
        setSuccessMessage('User settings successfully saved');
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000); // Closes the success message after 3 seconds
        setIsSaving(false);
      }
    } catch (error) {
      console.error('Error saving user details:', error);
      setError('Error saving user details. Please try again later.');
      setIsSaving(false);
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const toggleShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <div className="user-settings-container">
      <h2>User Settings</h2>
      {successMessage && <Alert variant="success" onClose={() => setSuccessMessage('')} dismissible>{successMessage}</Alert>}
      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      <Form onSubmit={handleSubmit}>
        <Form.Group controlId="userName">
          <Form.Label>Username</Form.Label>
          <div className="username-value">{user.user_name}</div>
        </Form.Group>
        <div className="form-row">
          <div className="form-col">
            <Form.Group controlId="firstName">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                type="text"
                name="user_first_name"
                value={user.user_first_name}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
          </div>
          <div className="form-col">
            <Form.Group controlId="lastName">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                type="text"
                name="user_last_name"
                value={user.user_last_name}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
          </div>
        </div>
        <div className="form-row">
          <div className="form-col">
            <Form.Group controlId="password">
              <Form.Label>Password</Form.Label>
              <div className="input-group">
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <div className="input-group-append">
                  <Button
                    variant="outline-secondary"
                    onClick={toggleShowPassword}
                    aria-label={showPassword ? 'Hide Password' : 'Show Password'}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </Button>
                </div>
              </div>
              <Form.Text className="text-muted">
                Enter a new password here to change it
              </Form.Text>
            </Form.Group>
          </div>
          <div className="form-col">
            <Form.Group controlId="confirmPassword">
              <Form.Label>Confirm Password</Form.Label>
              <div className="input-group">
                <Form.Control
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <div className="input-group-append">
                  <Button
                    variant="outline-secondary"
                    onClick={toggleShowConfirmPassword}
                    aria-label={showConfirmPassword ? 'Hide Password' : 'Show Password'}
                  >
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </Button>
                </div>
              </div>
            </Form.Group>
          </div>
        </div>
        <div className="form-actions">
          <Button variant="primary" type="submit" disabled={isSaving} className={isSaving ? 'saving' : ''}>
            Save Changes
          </Button>
          <Button variant="secondary" as={Link} to="/">
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default UserSettings;
